import { runUploadTransport } from '../utils/uploadTransport';
import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { buildUploadUrl, resolveUploadTarget, withAuthHeaders, getAuthToken } from '../utils/uploadRegion';
import { showToast } from '../components/NotificationTray';

const UploadContext = createContext(null);

// Performance tuning constants
const MAX_CONCURRENT_UPLOADS = 15;
const MAX_RETRY_ATTEMPTS = 3;
const RETRY_BASE_DELAY = 1000;
const USE_PRESIGNED_UPLOAD = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_PRESIGNED_UPLOAD !== 'false');
const MAX_FILE_MB = Number((typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_MAX_UPLOAD_MB) || 250);
const MAX_FILE_BYTES = Math.max(1, MAX_FILE_MB) * 1024 * 1024;

export function UploadProvider({ children }) {
  // Upload state
  const [files, setFiles] = useState([]);
  const [progress, setProgress] = useState({});
  const [uploading, setUploading] = useState(false);
  const [uploadTarget, setUploadTarget] = useState({ baseUrl: '', fallbacks: [], source: 'default' });
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelMinimized, setPanelMinimized] = useState(false);
  const [albumId, setAlbumId] = useState(null);
  const [libraryScope, setLibraryScope] = useState('library');
  const [duplicates, setDuplicates] = useState([]);
  const [authExpired, setAuthExpired] = useState(false);
  const [uploadStartTime, setUploadStartTime] = useState(null);

  // External sync state - for BatchUploader to push its state to the indicator
  const [externalStats, setExternalStats] = useState(null);

  // Refs
  const keepAliveRef = useRef(null);
  const uploadStartRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Resolve upload target on mount
  useEffect(() => {
    resolveUploadTarget().then((target) => {
      if (target) setUploadTarget(target);
    });
  }, []);

  const uploadTargets = React.useMemo(() => {
    const targets = [];
    if (uploadTarget?.baseUrl) targets.push(uploadTarget.baseUrl);
    if (Array.isArray(uploadTarget?.fallbacks)) targets.push(...uploadTarget.fallbacks);
    targets.push('');
    return Array.from(new Set(targets.filter((item) => item !== null && item !== undefined)));
  }, [uploadTarget]);

  // Keep-alive to prevent session expiration
  const startKeepAlive = useCallback(() => {
    if (keepAliveRef.current) return;
    const ping = async () => {
      try {
        await fetch('/api/me', {
          credentials: 'include',
          headers: withAuthHeaders()
        });
      } catch {}
    };
    ping();
    keepAliveRef.current = setInterval(ping, 2 * 60 * 1000);
  }, []);

  const stopKeepAlive = useCallback(() => {
    if (keepAliveRef.current) {
      clearInterval(keepAliveRef.current);
      keepAliveRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => stopKeepAlive();
  }, [stopKeepAlive]);

  const uploadFetch = useCallback((path, options = {}, baseUrl = uploadTarget?.baseUrl || '') => {
    const headers = withAuthHeaders(options.headers || {});
    return fetch(buildUploadUrl(path, baseUrl), {
      credentials: 'include',
      ...options,
      headers
    });
  }, [uploadTarget]);

  const shouldFallback = useCallback((err) => {
    const status = err?.status;
    if (!status) return true;
    if (status === 401) {
      setAuthExpired(true);
      return false;
    }
    if (status >= 500) return true;
    if (status === 429) return true;
    return false;
  }, []);

  // Add files to upload queue
  const addFiles = useCallback((newFiles, options = {}) => {
    const { albumId: newAlbumId, libraryScope: newLibraryScope } = options;

    // Filter valid files
    const validFiles = [];
    const oversized = [];

    for (const file of newFiles) {
      if (file.size > MAX_FILE_BYTES) {
        oversized.push(file);
      } else {
        validFiles.push(file);
      }
    }

    if (oversized.length > 0) {
      showToast(
        `${oversized.length} file(s) exceed ${MAX_FILE_MB}MB limit`,
        'These files were not added to the queue.',
        'warning'
      );
    }

    if (validFiles.length > 0) {
      setFiles(prev => [...prev, ...validFiles]);
      if (newAlbumId) setAlbumId(newAlbumId);
      if (newLibraryScope) setLibraryScope(newLibraryScope);
      setPanelOpen(true);
      setPanelMinimized(false);
    }

    return { added: validFiles.length, oversized: oversized.length };
  }, []);

  // Remove file from queue
  const removeFile = useCallback((index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
    setProgress(prev => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  }, []);

  // Clear completed uploads
  const clearCompleted = useCallback(() => {
    const completedIndices = new Set();
    Object.entries(progress).forEach(([idx, p]) => {
      if (p?.status === 'complete' || p?.status === 'error') {
        completedIndices.add(Number(idx));
      }
    });

    setFiles(prev => prev.filter((_, i) => !completedIndices.has(i)));
    setProgress(prev => {
      const next = {};
      Object.entries(prev).forEach(([idx, p]) => {
        if (!completedIndices.has(Number(idx))) {
          next[idx] = p;
        }
      });
      return next;
    });
  }, [progress]);

  // Start uploads
  const startUpload = useCallback(async () => {
    if (files.length === 0 || uploading) return;

    setUploading(true);
    setAuthExpired(false);
    setDuplicates([]);
    setUploadStartTime(Date.now());
    startKeepAlive();

    const uploadedCount = { success: 0, failed: 0, processing: 0 };

    // Check for duplicates
    try {
      const checkRes = await fetch('/api/images/check-duplicates', {
        method: 'POST',
        credentials: 'include',
        headers: withAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          files: files.map((f, i) => ({ filename: f.name, size: f.size, index: i }))
        })
      });
      if (checkRes.ok) {
        const { duplicates: dupes } = await checkRes.json();
        if (dupes?.length > 0) {
          setDuplicates(dupes);
          showToast(`${dupes.length} file(s) already exist in your library`, '', 'warning');
        }
      }
    } catch (e) {
      console.warn('Duplicate check failed:', e);
    }

    // Upload queue
    const uploadQueue = files.map((file, index) => ({ file, index }));
    const inProgress = [];

    const uploadFile = async ({ file, index }) => {
      let lastError = null;

      for (let attempt = 1; attempt <= MAX_RETRY_ATTEMPTS; attempt++) {
        try {
          const statusPrefix = attempt > 1 ? `retry ${attempt}/${MAX_RETRY_ATTEMPTS}` : 'uploading';
          setProgress(prev => ({ ...prev, [index]: { status: statusPrefix, percent: 0 } }));

          await runUploadTransport({
            presignedEnabled: USE_PRESIGNED_UPLOAD,
            presigned: () => uploadFilePresigned(file, index, uploadedCount),
            traditional: () => uploadFileTraditional(file, index, uploadedCount)
          });
          return;

        } catch (error) {
          lastError = error;
          console.warn(`Upload attempt ${attempt}/${MAX_RETRY_ATTEMPTS} failed for ${file.name}:`, error.message);

          if (attempt < MAX_RETRY_ATTEMPTS) {
            const delay = RETRY_BASE_DELAY * Math.pow(2, attempt - 1);
            setProgress(prev => ({ ...prev, [index]: { status: `waiting to retry (${Math.round(delay/1000)}s)`, percent: 0 } }));
            await new Promise(resolve => setTimeout(resolve, delay));
          }
        }
      }

      setProgress(prev => ({ ...prev, [index]: { status: 'error', percent: 0 } }));
      uploadedCount.failed++;
    };

    // Process with concurrency limit
    while (uploadQueue.length > 0 || inProgress.length > 0) {
      while (uploadQueue.length > 0 && inProgress.length < MAX_CONCURRENT_UPLOADS) {
        const item = uploadQueue.shift();
        const uploadPromise = uploadFile(item).finally(() => {
          const idx = inProgress.indexOf(uploadPromise);
          if (idx !== -1) inProgress.splice(idx, 1);
        });
        inProgress.push(uploadPromise);
      }

      if (inProgress.length > 0) {
        await Promise.race(inProgress);
      }
    }

    setUploading(false);
    stopKeepAlive();

    // Show completion notification
    const elapsed = Date.now() - (uploadStartRef.current || Date.now());
    const mins = Math.floor(elapsed / 60000);
    const secs = Math.round((elapsed % 60000) / 1000);
    const timeStr = mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;

    if (uploadedCount.failed === 0) {
      showToast(
        `Upload complete`,
        `${uploadedCount.success} photos uploaded in ${timeStr}`,
        'success'
      );
    } else {
      showToast(
        `Upload finished with errors`,
        `${uploadedCount.success} succeeded, ${uploadedCount.failed} failed`,
        'warning'
      );
    }
  }, [files, uploading, uploadFetch, startKeepAlive, stopKeepAlive]);

  // Presigned upload helper
  async function uploadFilePresigned(file, index, uploadedCount) {
    const presignedRes = await uploadFetch('/api/images/presigned', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        filename: file.name,
        contentType: file.type || 'application/octet-stream',
        size: file.size,
        library_scope: libraryScope
      })
    });

    if (!presignedRes.ok) {
      const error = new Error(`Presigned request failed: ${presignedRes.status}`);
      error.status = presignedRes.status;
      throw error;
    }

    const { uploadUrl, confirmToken, s3Key } = await presignedRes.json();

    // Upload to S3
    await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percent = Math.round((e.loaded / e.total) * 100);
          setProgress(prev => ({ ...prev, [index]: { status: 'uploading', percent } }));
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          const error = new Error(`S3 upload failed: ${xhr.status}`);
          error.status = xhr.status;
          reject(error);
        }
      });

      xhr.addEventListener('error', () => reject(new Error('Network error')));
      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
      xhr.send(file);
    });

    // Confirm upload
    const confirmRes = await uploadFetch('/api/images/confirm', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ confirmToken, albumId, library_scope: libraryScope })
    });

    if (!confirmRes.ok) {
      const error = new Error(`Confirm failed: ${confirmRes.status}`);
      error.status = confirmRes.status;
      throw error;
    }

    const response = await confirmRes.json();
    const isProcessing = response.processing_status === 'queued' || response.processing_status === 'pending';

    setProgress(prev => ({
      ...prev,
      [index]: {
        status: isProcessing ? 'processing' : 'complete',
        percent: 100,
        needsProcessing: isProcessing,
        imageId: response.id,
        filename: response.filename
      }
    }));

    uploadedCount.success++;
    if (isProcessing) uploadedCount.processing++;
  }

  // Traditional upload helper
  async function uploadFileTraditional(file, index, uploadedCount) {
    for (const baseUrl of uploadTargets) {
      try {
        await new Promise((resolve, reject) => {
          const formData = new FormData();
          formData.append('image', file);
          if (albumId) formData.append('album_id', albumId);
          formData.append('library_scope', libraryScope);

          const xhr = new XMLHttpRequest();
          xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable) {
              const percent = Math.round((e.loaded / e.total) * 100);
              setProgress(prev => ({ ...prev, [index]: { status: 'uploading', percent } }));
            }
          });

          xhr.addEventListener('load', () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              const response = JSON.parse(xhr.responseText);
              const isProcessing = response.processing_status === 'queued' || response.processing_status === 'pending';

              setProgress(prev => ({
                ...prev,
                [index]: {
                  status: isProcessing ? 'processing' : 'complete',
                  percent: 100,
                  needsProcessing: isProcessing,
                  imageId: response.id,
                  filename: response.filename
                }
              }));

              uploadedCount.success++;
              if (isProcessing) uploadedCount.processing++;
              resolve();
            } else {
              const error = new Error(`Upload failed: ${xhr.status}`);
              error.status = xhr.status;
              reject(error);
            }
          });

          xhr.addEventListener('error', () => reject(new Error('Network error')));

          xhr.withCredentials = true;
          xhr.open('POST', buildUploadUrl('/api/images', baseUrl));
          const token = getAuthToken();
          if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
          xhr.send(formData);
        });
        return;
      } catch (err) {
        if (!shouldFallback(err)) throw err;
      }
    }
  }

  // Panel controls
  const openPanel = useCallback(() => {
    setPanelOpen(true);
    setPanelMinimized(false);
  }, []);

  const closePanel = useCallback(() => {
    if (uploading) {
      // Don't fully close, just minimize
      setPanelMinimized(true);
    } else {
      // Clear external stats when fully closing so next upload starts fresh
      setExternalStats(null);
      setPanelOpen(false);
      setPanelMinimized(false);
    }
  }, [uploading]);

  // Clear stale state - called when clicking indicator after completion
  const dismissIndicator = useCallback(() => {
    setExternalStats(null);
    setPanelMinimized(false);
    setPanelOpen(false);
  }, []);

  const minimizePanel = useCallback(() => {
    setPanelMinimized(true);
  }, []);

  const maximizePanel = useCallback(() => {
    setPanelMinimized(false);
  }, []);

  // Sync state from external uploader (BatchUploader)
  const syncState = useCallback((stats) => {
    setExternalStats(previous => {
      if (previous && stats && Object.keys(stats).every(key => previous[key] === stats[key]) && Object.keys(previous).length === Object.keys(stats).length) return previous;
      return stats;
    });
    // If external uploader is uploading, mark panel as open
    if (stats?.uploading && stats?.totalFiles > 0) {
      setPanelOpen(true);
    }
    // If external uploader completed, we can keep minimized state
    if (stats?.isComplete) {
      // Upload done - stats will reflect final state
    }
  }, []);

  // Computed values - prefer external stats when available (from BatchUploader)
  const internalTotalFiles = files.length;
  const progressList = Object.values(progress);
  const internalCompletedCount = progressList.filter(p => p?.status === 'complete').length;
  const internalProcessingCount = progressList.filter(p => p?.status === 'processing').length;
  const internalFailedCount = progressList.filter(p => p?.status === 'error').length;
  const internalUploadedCount = internalCompletedCount + internalProcessingCount;
  const internalIsComplete = internalCompletedCount + internalFailedCount >= internalTotalFiles
    && internalTotalFiles > 0
    && !uploading
    && internalProcessingCount === 0;

  // Use external stats if available (BatchUploader syncs here)
  const totalFiles = externalStats?.totalFiles ?? internalTotalFiles;
  const completedCount = externalStats?.completedCount ?? internalCompletedCount;
  const processingCount = externalStats?.processingCount ?? internalProcessingCount;
  const failedCount = externalStats?.failedCount ?? internalFailedCount;
  const uploadedCount = externalStats?.uploadedCount ?? internalUploadedCount;
  const isComplete = externalStats?.isComplete ?? internalIsComplete;
  const isUploading = externalStats?.uploading ?? uploading;
  const attentionCount = externalStats?.attentionCount ?? failedCount;

  const value = {
    // State
    files,
    progress,
    uploading: isUploading,
    panelOpen,
    panelMinimized,
    albumId,
    libraryScope,
    duplicates,
    authExpired,
    uploadStartTime,
    externalStats,

    // Computed
    totalFiles,
    completedCount,
    processingCount,
    failedCount,
    attentionCount,
    uploadedCount,
    isComplete,

    // Actions
    addFiles,
    removeFile,
    clearCompleted,
    startUpload,
    openPanel,
    closePanel,
    minimizePanel,
    maximizePanel,
    dismissIndicator,
    setAlbumId,
    setLibraryScope,
    syncState,
  };

  return (
    <UploadContext.Provider value={value}>
      {children}
    </UploadContext.Provider>
  );
}

export function useUpload() {
  const context = useContext(UploadContext);
  // Return null instead of throwing - allows usage outside provider (e.g., public routes)
  return context;
}

export default UploadContext;
